/**
 * "Over", "watch" and "no ratio to read", decided once.
 *
 * §3 asks the same question in four places — whether the card is ochre, what the
 * answer sentence says, which meters are drawn, and what the hero's third tile
 * counts. Written out at each of them, three of the four had slightly different
 * predicates: the card and the sentence read `under_scoped`, the meters read
 * `under_scoped && ratio !== null`. They cannot disagree against today's read model
 * (`analytics-under-scoped.ts` derives `months_over` from a non-null ratio, so
 * `under_scoped` implies one), but the page was written as though they could — and if
 * they ever did, the card would go ochre above a sentence counting two packages and a
 * column drawing one meter.
 *
 * `.docs/00-index.md` §5's rule for a state check: one place, and every caller asks it.
 */

import type { HealthData } from './data.js'

export type ScopeRow = HealthData['clients'][number]

export type ScopePartition = {
  /** Past target for a second month running — a re-scope, and the ochre. */
  readonly over: readonly ScopeRow[]
  /** Past target this month only. Folded: onboarding always runs hot. */
  readonly watch: readonly ScopeRow[]
  /** Billed nothing this month, so `ratio` is null and there is no meter to draw. */
  readonly unpriced: readonly ScopeRow[]
}

export function partitionScope(health: HealthData): ScopePartition {
  return {
    over: health.clients.filter((one) => one.under_scoped && one.ratio !== null),
    watch: health.clients.filter((one) => one.watch && one.ratio !== null),
    unpriced: health.clients.filter((one) => one.ratio === null),
  }
}
